import {
  WORKSPACE_AGENT_ANCHOR_POSITIONS,
  type WorkspaceAgentAnchorPosition,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentAnchorPositions';
import { buildWorkspaceAgentGuidedVariantSkipOtherFilesClause } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePromptPhrases';
import {
  resolveWorkspaceAgentGuidedVariantPromptRenameSuffix,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  type WorkspaceAgentGuidedVariantSaveDestination,
  withWorkspaceAgentGuidedVariantSaveDestinationAndRename,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';
import {
  parseWorkspaceAgentSmartResizeQuery,
  sameWorkspaceAgentSmartResizeSize,
  sizeSetIsFullySelected,
  toggleWorkspaceAgentSmartResizeSet,
  toggleWorkspaceAgentSmartResizeSize,
  WORKSPACE_AGENT_SMART_RESIZE_MAX_SIDE,
  WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES,
  WORKSPACE_AGENT_SMART_RESIZE_MIN_SIDE,
  WORKSPACE_AGENT_SMART_RESIZE_SHAPES,
  type WorkspaceAgentSmartResizeSavedSize,
  type WorkspaceAgentSmartResizeShapeOption,
  type WorkspaceAgentSmartResizeSize,
  type WorkspaceAgentSmartResizeSizeSet,
  workspaceAgentSmartResizeRatioLabel,
  workspaceAgentSmartResizeSizeKey,
  workspaceAgentSmartResizeSizeLabel,
  workspaceAgentSmartResizeSizeText,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

export type WorkspaceAgentScaleConstraintSize = {
  by: 'height' | 'long-side' | 'width';
  px: number;
};

export type WorkspaceAgentUpscaleSize = {
  by: 'upscale';
  factor: 2 | 3 | 4;
};

export type WorkspaceAgentWebDownscaleId = 'large' | 'medium' | 'thumbnail';

export type WorkspaceAgentWebDownscaleSize = {
  by: 'web-downscale';
  id: WorkspaceAgentWebDownscaleId;
  px: number;
};

export type WorkspaceAgentScaleOrCropSize =
  | WorkspaceAgentScaleConstraintSize
  | WorkspaceAgentSmartResizeSize
  | WorkspaceAgentUpscaleSize
  | WorkspaceAgentWebDownscaleSize;

export type WorkspaceAgentScaleOrCropInputMode = 'exact' | 'height' | 'long-side' | 'search' | 'width';

export type WorkspaceAgentScaleOrCropOrigin = WorkspaceAgentAnchorPosition;

export type WorkspaceAgentScaleOrCropOriginOption = {
  id: WorkspaceAgentScaleOrCropOrigin;
  label: string;
  x: number;
  y: number;
};

type WorkspaceAgentScaleOrCropOriginLayout = Omit<WorkspaceAgentScaleOrCropOriginOption, 'id'>;

/** Crop keeps the short edge labels (Top, Left) while the ids match watermark placement. */
const WORKSPACE_AGENT_SCALE_OR_CROP_ORIGIN_LAYOUT = {
  'bottom-center': { label: 'Bottom', x: 0.5, y: 1 },
  'bottom-left': { label: 'Bottom left', x: 0, y: 1 },
  'bottom-right': { label: 'Bottom right', x: 1, y: 1 },
  center: { label: 'Center', x: 0.5, y: 0.5 },
  'middle-left': { label: 'Left', x: 0, y: 0.5 },
  'middle-right': { label: 'Right', x: 1, y: 0.5 },
  'top-center': { label: 'Top', x: 0.5, y: 0 },
  'top-left': { label: 'Top left', x: 0, y: 0 },
  'top-right': { label: 'Top right', x: 1, y: 0 },
} as const satisfies Record<WorkspaceAgentAnchorPosition, WorkspaceAgentScaleOrCropOriginLayout>;

export const WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS: readonly WorkspaceAgentScaleOrCropOriginOption[] =
  WORKSPACE_AGENT_ANCHOR_POSITIONS.map((id) => ({
    id,
    ...WORKSPACE_AGENT_SCALE_OR_CROP_ORIGIN_LAYOUT[id],
  }));

export const WORKSPACE_AGENT_DEFAULT_SCALE_OR_CROP_ORIGIN: WorkspaceAgentScaleOrCropOrigin = 'center';

export const WORKSPACE_AGENT_SCALE_OR_CROP_UPSCALES: readonly WorkspaceAgentUpscaleSize[] = [
  { by: 'upscale', factor: 2 },
  { by: 'upscale', factor: 3 },
  { by: 'upscale', factor: 4 },
];

export const WORKSPACE_AGENT_SCALE_OR_CROP_WEB_DOWNSCALES: readonly WorkspaceAgentWebDownscaleSize[] = [
  { by: 'web-downscale', id: 'large', px: 2048 },
  { by: 'web-downscale', id: 'medium', px: 1200 },
  { by: 'web-downscale', id: 'thumbnail', px: 600 },
];

export const WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS: Record<WorkspaceAgentWebDownscaleId, string> = {
  large: 'Large',
  medium: 'Medium',
  thumbnail: 'Thumbnail',
};

const CONSTRAINT_LABELS: Record<WorkspaceAgentScaleConstraintSize['by'], string> = {
  height: 'height',
  'long-side': 'long side',
  width: 'width',
};

const clampSide = (side: number): number =>
  Math.min(WORKSPACE_AGENT_SMART_RESIZE_MAX_SIDE, Math.max(WORKSPACE_AGENT_SMART_RESIZE_MIN_SIDE, side));

export const isWorkspaceAgentSmartResizeCompatibleSize = (
  size: WorkspaceAgentScaleOrCropSize,
): size is WorkspaceAgentSmartResizeSize => size.by === 'exact-size' || size.by === 'ratio';

export const workspaceAgentSmartResizeSizesFromScaleOrCrop = (
  sizes: readonly WorkspaceAgentScaleOrCropSize[],
): WorkspaceAgentSmartResizeSize[] => sizes.filter(isWorkspaceAgentSmartResizeCompatibleSize);

export const isWorkspaceAgentScaleOrCropCropSize = (
  size: WorkspaceAgentScaleOrCropSize,
): size is WorkspaceAgentSmartResizeSize => isWorkspaceAgentSmartResizeCompatibleSize(size);

export const workspaceAgentScaleOrCropSizeKey = (size: WorkspaceAgentScaleOrCropSize): string => {
  switch (size.by) {
    case 'exact-size':
    case 'ratio':
      return workspaceAgentSmartResizeSizeKey(size);
    case 'height':
    case 'long-side':
    case 'width':
      return `${size.by}:${size.px}`;
    case 'upscale':
      return `upscale:${size.factor}`;
    case 'web-downscale':
      return `web:${size.id}`;
  }
};

export const sameWorkspaceAgentScaleOrCropSize = (
  left: WorkspaceAgentScaleOrCropSize,
  right: WorkspaceAgentScaleOrCropSize,
): boolean => {
  if (isWorkspaceAgentSmartResizeCompatibleSize(left) && isWorkspaceAgentSmartResizeCompatibleSize(right)) {
    return sameWorkspaceAgentSmartResizeSize(left, right);
  }

  return workspaceAgentScaleOrCropSizeKey(left) === workspaceAgentScaleOrCropSizeKey(right);
};

export const workspaceAgentScaleOrCropSizeText = (size: WorkspaceAgentScaleOrCropSize): string => {
  switch (size.by) {
    case 'exact-size':
    case 'ratio':
      return workspaceAgentSmartResizeSizeText(size);
    case 'height':
    case 'long-side':
    case 'width':
      return `${size.px} ${CONSTRAINT_LABELS[size.by]}`;
    case 'upscale':
      return `${size.factor}x`;
    case 'web-downscale':
      return `${size.px} px`;
  }
};

export const workspaceAgentScaleOrCropSizeLabel = (size: WorkspaceAgentScaleOrCropSize): string => {
  switch (size.by) {
    case 'exact-size':
    case 'ratio': {
      const ratioName = size.by === 'ratio' ? workspaceAgentSmartResizeRatioLabel(size) : null;

      return ratioName ?? workspaceAgentSmartResizeSizeLabel(size);
    }
    case 'upscale':
      return `${size.factor}x`;
    case 'web-downscale':
      return WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS[size.id];
    case 'height':
    case 'long-side':
    case 'width':
      return workspaceAgentScaleOrCropSizeText(size);
  }
};

export type WorkspaceAgentScaleOrCropSizeShape = {
  height: number;
  width: number;
};

export const workspaceAgentScaleOrCropSizeShape = (
  size: WorkspaceAgentScaleOrCropSize,
): WorkspaceAgentScaleOrCropSizeShape | null => {
  switch (size.by) {
    case 'exact-size':
    case 'ratio':
      return { height: size.height, width: size.width };
    case 'width':
      return { height: 1, width: 2 };
    case 'height':
      return { height: 2, width: 1 };
    case 'long-side':
    case 'web-downscale':
      return { height: 2, width: 3 };
    case 'upscale':
      return null;
  }
};

export const isWorkspaceAgentScaleOrCropSizeSelected = (
  sizes: readonly WorkspaceAgentScaleOrCropSize[],
  size: WorkspaceAgentScaleOrCropSize,
): boolean => sizes.some((selected) => sameWorkspaceAgentScaleOrCropSize(selected, size));

export const toggleWorkspaceAgentScaleOrCropSet = (
  sizes: readonly WorkspaceAgentScaleOrCropSize[],
  sizeSet: WorkspaceAgentSmartResizeSizeSet,
): WorkspaceAgentScaleOrCropSize[] => {
  const smartSizes = workspaceAgentSmartResizeSizesFromScaleOrCrop(sizes);
  const otherSizes = sizes.filter((size) => !isWorkspaceAgentSmartResizeCompatibleSize(size));
  const nextSmart = toggleWorkspaceAgentSmartResizeSet(smartSizes, sizeSet);

  if (sizeSetIsFullySelected(smartSizes, sizeSet)) {
    return [...otherSizes, ...nextSmart];
  }

  const maxSmart = Math.max(0, WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES - otherSizes.length);

  return [...otherSizes, ...nextSmart.slice(0, maxSmart)];
};

export const toggleWorkspaceAgentScaleOrCropSize = (
  sizes: readonly WorkspaceAgentScaleOrCropSize[],
  size: WorkspaceAgentScaleOrCropSize,
): { added: boolean; sizes: WorkspaceAgentScaleOrCropSize[] } => {
  if (isWorkspaceAgentSmartResizeCompatibleSize(size) && sizes.every(isWorkspaceAgentSmartResizeCompatibleSize)) {
    return toggleWorkspaceAgentSmartResizeSize(sizes, size);
  }

  if (isWorkspaceAgentScaleOrCropSizeSelected(sizes, size)) {
    return {
      added: false,
      sizes: sizes.filter((selected) => !sameWorkspaceAgentScaleOrCropSize(selected, size)),
    };
  }

  if (sizes.length >= WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES) {
    return { added: false, sizes: [...sizes] };
  }

  return { added: true, sizes: [...sizes, size] };
};

export const parseWorkspaceAgentScaleOrCropQuery = (
  query: string,
  inputMode: WorkspaceAgentScaleOrCropInputMode,
): WorkspaceAgentScaleOrCropSize | null => {
  const normalized = query.trim().toLowerCase();

  if (inputMode === 'height' || inputMode === 'long-side' || inputMode === 'width') {
    const match = /^(\d+)\s*(?:px)?$/.exec(normalized);

    if (!match?.[1]) {
      return null;
    }

    return { by: inputMode, px: clampSide(Number(match[1])) };
  }

  return parseWorkspaceAgentSmartResizeQuery(query);
};

const includesQuery = (query: string, ...values: Array<string | null | undefined>): boolean =>
  values.some((value) => value?.toLowerCase().includes(query));

export type WorkspaceAgentScaleOrCropSearchResults = {
  crop: WorkspaceAgentSmartResizeShapeOption[];
  downscale: WorkspaceAgentWebDownscaleSize[];
  savedSizes: WorkspaceAgentSmartResizeSavedSize[];
  sets: WorkspaceAgentSmartResizeSizeSet[];
  typed: WorkspaceAgentScaleOrCropSize | null;
  upscale: WorkspaceAgentUpscaleSize[];
};

const EMPTY_WORKSPACE_AGENT_SCALE_OR_CROP_SEARCH_RESULTS: WorkspaceAgentScaleOrCropSearchResults = {
  crop: [],
  downscale: [],
  savedSizes: [],
  sets: [],
  typed: null,
  upscale: [],
};

export type WorkspaceAgentScaleOrCropEnterMatch =
  | { kind: 'set'; sizeSet: WorkspaceAgentSmartResizeSizeSet }
  | { kind: 'size'; size: WorkspaceAgentScaleOrCropSize };

export const matchWorkspaceAgentScaleOrCropEnter = ({
  inputMode,
  query,
  search,
}: {
  inputMode: WorkspaceAgentScaleOrCropInputMode;
  query: string;
  search: WorkspaceAgentScaleOrCropSearchResults;
}): WorkspaceAgentScaleOrCropEnterMatch | null => {
  const typed = search.typed ?? parseWorkspaceAgentScaleOrCropQuery(query, inputMode);

  if (typed) {
    return { kind: 'size', size: typed };
  }

  if (search.crop[0]) {
    return { kind: 'size', size: search.crop[0].size };
  }

  if (search.sets[0]) {
    return { kind: 'set', sizeSet: search.sets[0] };
  }

  if (search.savedSizes[0]) {
    return { kind: 'size', size: search.savedSizes[0].size };
  }

  if (search.upscale[0]) {
    return { kind: 'size', size: search.upscale[0] };
  }

  if (search.downscale[0]) {
    return { kind: 'size', size: search.downscale[0] };
  }

  return null;
};

export const searchWorkspaceAgentScaleOrCrop = ({
  inputMode,
  query,
  savedSets,
  savedSizes,
}: {
  inputMode: WorkspaceAgentScaleOrCropInputMode;
  query: string;
  savedSets: readonly WorkspaceAgentSmartResizeSizeSet[];
  savedSizes: readonly WorkspaceAgentSmartResizeSavedSize[];
}): WorkspaceAgentScaleOrCropSearchResults => {
  const normalized = query.trim().toLowerCase();
  const typed = normalized ? parseWorkspaceAgentScaleOrCropQuery(normalized, inputMode) : null;

  if (!normalized) {
    return EMPTY_WORKSPACE_AGENT_SCALE_OR_CROP_SEARCH_RESULTS;
  }

  if (inputMode === 'height' || inputMode === 'long-side' || inputMode === 'width') {
    return {
      ...EMPTY_WORKSPACE_AGENT_SCALE_OR_CROP_SEARCH_RESULTS,
      downscale: WORKSPACE_AGENT_SCALE_OR_CROP_WEB_DOWNSCALES.filter((size) =>
        includesQuery(normalized, WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS[size.id], `${size.px}`, `${size.px} px`),
      ),
      typed,
    };
  }

  return {
    crop: WORKSPACE_AGENT_SMART_RESIZE_SHAPES.filter((shape) => {
      const label = workspaceAgentSmartResizeSizeLabel(shape.size);
      const ratioName = workspaceAgentSmartResizeRatioLabel(shape.size);

      return includesQuery(normalized, label, ratioName, workspaceAgentSmartResizeSizeText(shape.size), shape.id);
    }),
    downscale: WORKSPACE_AGENT_SCALE_OR_CROP_WEB_DOWNSCALES.filter((size) =>
      includesQuery(
        normalized,
        WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS[size.id],
        `${size.px}`,
        `${size.px} px`,
        'downscale',
        'web',
      ),
    ),
    savedSizes: savedSizes.filter((saved) =>
      includesQuery(
        normalized,
        saved.name,
        workspaceAgentSmartResizeSizeLabel(saved.size),
        workspaceAgentSmartResizeSizeText(saved.size),
      ),
    ),
    sets: savedSets.filter((sizeSet) => includesQuery(normalized, sizeSet.name)),
    typed,
    upscale: WORKSPACE_AGENT_SCALE_OR_CROP_UPSCALES.filter((size) => {
      const label = `${size.factor}x AI upscale`;

      return includesQuery(normalized, label, `${size.factor}x`, 'upscale');
    }),
  };
};

export const expandedWorkspaceAgentScaleOrCropGroups = (): Set<string> => new Set(['saved']);

export type WorkspaceAgentScaleOrCropWindow = {
  height: number;
  left: number;
  top: number;
  width: number;
};

/** The outer frame stands in for the source image. The crop window is drawn inside it. */
export const WORKSPACE_AGENT_CROP_ORIGIN_SHORT_SIDE = 80;
export const WORKSPACE_AGENT_CROP_ORIGIN_LONG_SIDE = 120;

export const getWorkspaceAgentScaleOrCropWindow = ({
  frameHeight,
  frameWidth,
  origin,
  size,
}: {
  frameHeight: number;
  frameWidth: number;
  origin: WorkspaceAgentScaleOrCropOrigin;
  size: WorkspaceAgentSmartResizeSize;
}): WorkspaceAgentScaleOrCropWindow => {
  const originOption =
    WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS.find((option) => option.id === origin) ??
    WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS.find(
      (option) => option.id === WORKSPACE_AGENT_DEFAULT_SCALE_OR_CROP_ORIGIN,
    ) ??
    WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS[0];
  const cropAspect = size.width / size.height;
  const frameAspect = frameWidth / frameHeight;
  const width = cropAspect > frameAspect ? frameWidth : frameHeight * cropAspect;
  const height = cropAspect > frameAspect ? frameWidth / cropAspect : frameHeight;

  return {
    height,
    left: (frameWidth - width) * originOption.x,
    top: (frameHeight - height) * originOption.y,
    width,
  };
};

export const workspaceAgentScaleOrCropOriginPhrase = (origin: WorkspaceAgentScaleOrCropOrigin): string => {
  const option = WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS.find((item) => item.id === origin);

  return option ? option.label.toLowerCase() : 'center';
};

export const workspaceAgentScaleOrCropUpscaleChannelId = (factor: 2 | 3 | 4): string => `upscale-${factor}x`;

export const workspaceAgentScaleOrCropConstraintChannelId = (
  constraint: WorkspaceAgentScaleConstraintSize['by'],
): string => `scale-${constraint}`;

export type BuildWorkspaceAgentScaleOrCropPromptInput = {
  destination: WorkspaceAgentGuidedVariantSaveDestination;
  imageCount: number;
  origin: WorkspaceAgentScaleOrCropOrigin;
  output?: WorkspaceAgentGuidedVariantOutput;
  sizes: readonly WorkspaceAgentScaleOrCropSize[];
  skippedFileCount: number;
};

/**
 * Pixels, a ratio, or a scale factor. Preset and saved names stay in the picker.
 * Thumbnail keeps its name: "600 px" does not say what that size is for.
 */
const workspaceAgentScaleOrCropPromptSizePhrase = (size: WorkspaceAgentScaleOrCropSize): string => {
  if (size.by === 'web-downscale' && size.id === 'thumbnail') {
    return `${size.px} px thumbnail`;
  }

  return workspaceAgentScaleOrCropSizeText(size);
};

export const buildWorkspaceAgentScaleOrCropPrompt = ({
  destination,
  imageCount,
  origin,
  output = WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  sizes,
  skippedFileCount,
}: BuildWorkspaceAgentScaleOrCropPromptInput): string => {
  const subject = imageCount === 1 ? 'the selected image or video' : 'each selected image or video';
  const sizeList = sizes.map(workspaceAgentScaleOrCropPromptSizePhrase).join(', ');
  const hasCrop = sizes.some(isWorkspaceAgentScaleOrCropCropSize);
  const cropInstruction = hasCrop
    ? ` When cropping, start from the ${workspaceAgentScaleOrCropOriginPhrase(origin)}.`
    : '';
  const skipOtherFiles = buildWorkspaceAgentGuidedVariantSkipOtherFilesClause({
    appliesToPhrase: 'scale or crop only applies to images and videos',
    skippedFileCount,
  });

  return withWorkspaceAgentGuidedVariantSaveDestinationAndRename(
    `Scale or crop ${subject} for each of these sizes: ${sizeList}. Do not add overlay with size.${cropInstruction}${skipOtherFiles}`,
    destination,
    resolveWorkspaceAgentGuidedVariantPromptRenameSuffix('{size}', output),
    { sizeCount: sizes.length },
  );
};
